import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getOmniStatusMap } from '../api/profile'
import { useAuth } from '../auth/authContext'
import type { OmniStatusMap } from '../types'

const CACHE_KEY = 'omni:status-map'

/** Last-known status map from a previous session, or undefined if none/invalid. */
function readCachedStatusMap(): OmniStatusMap | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as OmniStatusMap) : undefined
  } catch {
    return undefined
  }
}

/**
 * The Omnidek status code → display name table (GET /me/omni/statusmap).
 *
 * The table is static server-side reference data, so it's held fresh for a day
 * and mirrored to localStorage for use as `initialData` — the same trick
 * useProfile uses. Without it a refresh would resolve the caller's status to the
 * `prospect` fallback for a frame (the map lands after the asset row), flipping
 * the shell and home composition out from under the user. `initialDataUpdatedAt:
 * 0` still marks the cached copy stale so a fresh fetch runs in the background.
 */
export function useOmniStatusMap() {
  const { authenticated } = useAuth()
  const query = useQuery({
    queryKey: ['omni', 'status-map'],
    queryFn: getOmniStatusMap,
    enabled: authenticated, // requires the omnideck:read scope on the bearer token
    staleTime: 24 * 60 * 60 * 1000,
    initialData: readCachedStatusMap,
    initialDataUpdatedAt: 0,
  })

  useEffect(() => {
    if (!query.isSuccess) return
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(query.data))
    } catch {
      // storage full / unavailable — cache is best-effort, ignore
    }
  }, [query.isSuccess, query.data])

  return query
}
